/**
 * Д-20 — «Чат уже открыт на другом устройстве. Перенести сюда?»
 *
 * Один текст на два контроллера (1:1 и комната) и на обе стороны переноса.
 * Модуль намеренно ЧИСТЫЙ (без React и без крипты): его читают
 * `chatController`/`roomController`, которые обязаны остаться лёгкими —
 * `olmClient` тянет WASM 7,8 МБ и грузится только динамически.
 *
 * 🔴 Почему вопрос вообще есть. Регистрация устройства ревокует прежнее
 * МОЛЧА (backend `register_device`), и зовётся она раньше любой проверки
 * доступа. Без вопроса человек, открывший кабинет в новом браузере, выключает
 * себе телефон, ничего не нажимая (на бою так и произошло 25.08: устройство
 * id 2 ревокнуто в ту же секунду, когда создано id 4).
 *
 * 🔴 Что именно теряется — проверено по коду, а не по здравому смыслу
 * (ревью 03.09 поймало здесь враньё в первой редакции текста):
 * - показанная здесь переписка НЕ пропадает: история лежит в общей базе
 *   `total-msg` по (conversationId|roomId) и к did не привязана
 *   (`msgStore.js`), только олм-стор именуется по устройству;
 * - пропадает ПРИШЕДШЕЕ, пока чат работал на другом устройстве: конверты 1:1
 *   адресованы устройству-получателю, а регистрация нового устройства
 *   двигает границу видимости комнаты (`sync_visibility_on_device`). Эти
 *   сообщения сюда не приедут никогда и без всякой метки.
 * Поэтому текст обещает ровно это, а не «чистый лист».
 *
 * 🔴 «Подключено», а не «открыт»: сервер хранит только `created_at` и
 * `revoked_at` — «чат сейчас открыт» и «устройство зарегистрировано 25.08 и
 * с тех пор лежит в ящике» он не различает.
 */

import { cryptoDbNames } from './deviceTransfer';

/**
 * Д-20: снести олм-стор устройства, которое мы только что оставили.
 *
 * 🔴 Зачем. Каждый перенос заводит новый did, а с ним новую базу
 * `total-msg-<принципал>-<did>`. Без уборки они копятся с каждым «туда-сюда»
 * телефон↔браузер, и это не только место: в брошенной базе остаётся ключевой
 * материал ревокнутого устройства, тогда как человеку сказано «ключи остались
 * там». Упереться в квоту IndexedDB значит перестать открывать чат вовсе.
 *
 * Терять нечего: did либо не регистрировался, либо выжжен сервером навсегда,
 * а переписка лежит отдельно, в общей базе `total-msg`.
 *
 * Best-effort: удаление ждёт закрытия соединений (старый контроллер как раз
 * останавливается), и ни отказ, ни `onblocked` не имеют права мешать
 * переносу — поэтому без await и без исключений наружу.
 */
export function forgetDeviceStore(storeName) {
  try {
    // 🔴 Крипто-стор — это ДВЕ базы с суффиксами, а не одна с голым именем
    // (ревью р.2, Low). Про суффиксы узнали уже ПОСЛЕ Д-20, на прогоне §7а
    // Д-3, и до этой правки уборка не сносила ничего: брошенный ключевой
    // материал копился с каждым «туда-сюда», ровно вопреки тому, ради чего
    // функция написана. Новая кнопка «Подключить заново» — ещё один её
    // вызыватель, так что копилось бы теперь и на нём.
    for (const name of cryptoDbNames(storeName)) {
      const req = globalThis.indexedDB?.deleteDatabase(name);
      if (req) { req.onerror = () => {}; req.onblocked = () => {}; }
    }
  } catch { /* приватный режим, запрет хранилища — перенос это не отменяет */ }
}

/** «подключено 25.08.2026» — дату берём из created_at устройства. */
export function connectedOn(createdAt) {
  if (!createdAt) return null;
  const d = new Date(createdAt);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/**
 * Д-3 «хвост»: 409 на подъёме — крипто-стор погиб, did цел.
 *
 * 🔴 Текст не обещает лишнего. Что известно точно: ключей на этом устройстве
 * больше нет, а сервер помнит прежние и новые под тем же did не примет —
 * значит сам по себе чат здесь не поднимется НИКОГДА, сколько ни обновляй
 * страницу. Что при этом цело: переписка в общей базе браузера (`total-msg`)
 * и строка участника на сервере — поэтому «повреждены данные чата», а не
 * «переписка потеряна».
 *
 * Действия оставлены странице: у участника есть копия `.totalchat` (она
 * кладёт прежние ключи обратно и лечит по-настоящему), у владельца — новый
 * did. Текст об этом не говорит: страницы разные, и обещать чужую кнопку
 * общий модуль не имеет права.
 */
export function deviceIdentityLostError() {
  return {
    kind: 'device_identity_lost',
    text: 'Данные чата на этом устройстве повреждены: ключи пропали, а сервер'
      + ' помнит прежние. Обновление страницы не поможет — новые ключи под этим'
      + ' же устройством сервер не принимает. Сама переписка на месте.',
    retriable: false,
  };
}

/**
 * Ошибка подъёма для страницы: не «нет доступа», а вопрос с действием.
 * @param info ответ GET /msg/devices/mine
 */
export function deviceTakeoverError(info) {
  const when = connectedOn(info?.created_at);
  return {
    kind: 'other_device',
    device: info ?? null,
    text: `Чат подключён к другому устройству${when ? ` (с ${when})` : ''}.`
      + ' Перенести его сюда? На том устройстве чат перестанет работать.'
      + ' Сообщения, которые приходили, пока чат был там, сюда не попадут —'
      + ' ключи от них остались на том устройстве. Уже показанная здесь'
      + ' переписка останется на месте.',
    retriable: false,
  };
}
